import React, { useState, useEffect } from 'react';
import { getContactMessages, markMessageAsRead, deleteContactMessage } from '../../firebase/services';
import { FaTrash, FaEnvelope, FaEnvelopeOpen } from 'react-icons/fa';
import styles from './Messages.module.css';

function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const result = await getContactMessages();
      if (result.success) {
        setMessages(result.messages);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    
    // If message is not read, mark it as read
    if (!message.isRead) {
      try {
        await markMessageAsRead(message.id);
        // Update the messages list
        setMessages(messages.map(msg => 
          msg.id === message.id ? { ...msg, isRead: true } : msg
        ));
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      setIsDeleting(true);
      try {
        const result = await deleteContactMessage(messageId);
        if (result.success) {
          setMessages(messages.filter(msg => msg.id !== messageId));
          if (selectedMessage && selectedMessage.id === messageId) {
            setSelectedMessage(null);
          }
        } else {
          setError(result.error);
        }
      } catch (error) {
        console.error('Error deleting message:', error);
        setError('Failed to delete message');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className={styles.loading}>Loading messages...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.messagesContainer}>
      <h2 className={styles.messagesTitle}>Contact Messages</h2>
      
      <div className={styles.messagesList}>
        {messages.length === 0 ? (
          <div className={styles.noMessages}>No messages received yet</div>
        ) : (
          <div className={styles.messagesWrapper}>
            <div className={styles.messageItems}>
              {messages.map(message => (
                <div 
                  key={message.id} 
                  className={`${styles.messageItem} ${!message.isRead ? styles.unread : ''} ${selectedMessage?.id === message.id ? styles.selected : ''}`}
                  onClick={() => handleSelectMessage(message)}
                >
                  <div className={styles.messageIcon}>
                    {message.isRead ? <FaEnvelopeOpen /> : <FaEnvelope />}
                  </div>
                  <div className={styles.messagePreview}>
                    <div className={styles.messageHeader}>
                      <span className={styles.messageSender}>{message.name}</span>
                      <span className={styles.messageDate}>{formatDate(message.createdAt)}</span>
                    </div>
                    <div className={styles.messageSubject}>{message.subject}</div>
                    <div className={styles.messageExcerpt}>
                      {message.message.substring(0, 60)}
                      {message.message.length > 60 ? '...' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className={styles.messageDetail}>
              {selectedMessage ? (
                <div className={styles.selectedMessage}>
                  <div className={styles.messageActions}>
                    <button 
                      className={styles.deleteButton}
                      onClick={() => handleDeleteMessage(selectedMessage.id)}
                      disabled={isDeleting}
                    >
                      <FaTrash /> {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                  
                  <div className={styles.messageContent}>
                    <h3 className={styles.messageDetailSubject}>{selectedMessage.subject}</h3>
                    <div className={styles.messageDetailMeta}>
                      <p><strong>From:</strong> {selectedMessage.name} ({selectedMessage.email})</p>
                      <p><strong>Date:</strong> {formatDate(selectedMessage.createdAt)}</p>
                    </div>
                    <div className={styles.messageDivider}></div>
                    <div className={styles.messageDetailBody}>
                      {selectedMessage.message}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.noMessageSelected}>
                  <p>Select a message to view its contents</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Messages;
